import { orderBy } from "@progress/kendo-data-query";

import { DomainType } from "models/domain";
import { normalizeTaxId } from "scenes/ChoiceListsSettings/utils/taxId";
import { deleteEndpoint, get, post } from "utils/net";

import { mapChoiceLists } from "../encounter/encounterMapping";

import type { EncounterEntity } from "models/encounterEntity";
import type { FooterField, ProvidersImportJob } from "models/encounterServiceEntity";
import type { ChoiceListsState, IdDescriptionBase } from "models/patientEncounter";
import type {
  ChoiceListDataItem,
  ChoiceListDefaultValue,
  ChoiceListItem,
} from "scenes/ChoiceListsSettings/models/choiceListsSettings";
import type {
  ConversionFactorItem,
  ConversionFactorTypeItem,
} from "scenes/ChoiceListsSettings/models/providerConversionFactors";
import type { FacilityPreferences } from "../../models/facilitySettings";

const baseURL = process.env.REACT_APP_API_PATH;
const groupingURL = process.env.REACT_APP_TEE_GRP;

export const facilityToIdDescriptionBaseMapper = (
  facility: FacilityPreferences,
): IdDescriptionBase => ({
  id: facility.Id ?? "",
  title: facility.Description ?? "",
  CurrentUserPermissions: facility.CurrentUserPermissions,
  // GUI-2192: use ViewId
  ViewId: facility.ViewId,
});

const getFacilities = (selected?: string): Promise<IdDescriptionBase[]> => {
  const url = `${baseURL}/api/ChoiceLists/facilities${selected ? `?selected=${selected}` : ""}`;

  const extraOptions = {
    useExactPath: true,
  };

  return get(url, extraOptions).then((result: { body: FacilityPreferences[] }) => {
    const items: FacilityPreferences[] = result.body ?? [];
    const facilities: IdDescriptionBase[] = items.map(facilityToIdDescriptionBaseMapper);

    return orderBy(facilities, [{ field: "id", dir: "asc" }]);
  });
};

// not used now. Was used to load all choices lists
const getChoiceListDataByFacility = (
  id: string | number,
  choiceLists: ChoiceListsState,
): Promise<ChoiceListsState> => {
  const url = `${baseURL}/api/choicelists/facility/${id}`;

  const extraOptions = {
    useExactPath: true,
  };

  return get(url, extraOptions).then((result) => mapChoiceLists(result.body, choiceLists));
};

const providerChoiceListMapper = (item: any) => ({
  id: item.Id,
  name: item.Description,
  service: item.Service || "",
  npi: item.NPI || "",
  taxId: item.TaxId || "",
  isActive: item.IsActive ?? true,
  // GUI-2192: use ViewId
  ViewId: item.ViewId,
});

const serviceLocationChoiceListMapper = (item: any) => ({
  id: item.Id,
  name: item.Description,
  npi: item.NPI || "",
  placeOfService: item.PlaceOfService || "",
  isActive: item.IsActive ?? true,
  ViewId: item.ViewId,
});

// TODO: temp version until comboboxes are working: load all lists that must be initialized initially
const getInitialChoiceListDataByFacility = (
  facility: string,
  encounterType: string,
  encounter?: EncounterEntity,
): Promise<Record<string, unknown>[]> => {
  const isPracticeMode = encounter?.domain === DomainType.Practice;
  const choiceListsCalls: Promise<Record<string, unknown>>[] = [];

  if (!isPracticeMode) {
    choiceListsCalls.push(
      getChoiceListData(facility, "EncounterTypes", encounterType).then((results) => ({
        name: "encounterTypes",
        values: results,
      })),
    );

    choiceListsCalls.push(
      getChoiceListData(facility, "Services", encounter?.service).then((results) => ({
        name: "services",
        values: results,
      })),
    );

    choiceListsCalls.push(
      getChoiceListData(facility, "FinancialClasses", encounter?.financialClass).then(
        (results) => ({
          name: "financialClasses",
          values: results,
        }),
      ),
    );

    choiceListsCalls.push(
      getChoiceListData(facility, "SourceOfAdmissions", encounter?.ofa).then((results) => ({
        name: "ofa",
        values: results,
      })),
    );

    choiceListsCalls.push(
      getPayerFlags().then((results) => ({
        name: "payerFlags",
        values: results,
      })),
    );

    choiceListsCalls.push(
      getBillingNotes().then((results) => ({
        name: "billingNotes",
        values: results,
      })),
    );
  }

  if (isPracticeMode) {
    choiceListsCalls.push(
      getChoiceListData(
        facility,
        "LocationServices",
        encounter?.serviceLocation,
        serviceLocationChoiceListMapper,
        "Id",
      ).then((results) => ({
        name: "serviceLocations",
        values: results,
      })),
    );
  }

  choiceListsCalls.push(
    getChoiceListData(facility, "PatientStatuses", encounter?.patientStatus).then((results) => ({
      name: "patientStatuses",
      values: results,
    })),
  );

  choiceListsCalls.push(
    getChoiceListData(facility, "RecordStatuses", encounter?.recordStatus).then((results) => ({
      name: "recordStatuses",
      values: results,
    })),
  );

  choiceListsCalls.push(
    getChoiceListData(facility, "Sexes", encounter?.sex).then((results) => ({
      name: "sexes",
      values: results,
    })),
  );

  choiceListsCalls.push(
    getChoiceListData(
      facility,
      "Providers",
      undefined,
      providerChoiceListMapper,
      "name",
      true,
    ).then((results) => ({
      name: "providers",
      values: results,
    })),
  );

  return Promise.all(choiceListsCalls).then((results) => results);
};

const getChoiceListData = (
  facility: string,
  name: string,
  selected?: string,
  fieldsMapper?: (item: any) => any,
  sortField = "id",
  returnInactive = false,
): Promise<IdDescriptionBase[]> => {
  // Use skip and top fields in the future: skip=0&top=100&
  const params: string[] = [];
  if (selected) {
    params.push(`selected=${selected}`);
  }
  if (returnInactive) {
    params.push(`returnInactive=${returnInactive ? "true" : "false"}`);
  }

  const queryString = params.length ? `?${params.join("&")}` : "";
  const url = `${baseURL}/api/choicelists/choicelist/${facility}/${name}${queryString}`;

  const extraOptions = {
    useExactPath: true,
  };

  return get(url, extraOptions).then((result) => {
    const items = result.body || [];
    const unsorted = items.map((item: any) => {
      const newItem =
        fieldsMapper ?
          fieldsMapper(item)
        : {
            id: item.Id,
            title: item.Description,
            // GUI-2192: use ViewId
            ViewId: item.ViewId,
          };

      return newItem;
    });

    const sorted = sortField ? orderBy(unsorted, [{ field: sortField, dir: "asc" }]) : unsorted;

    return sorted;
  });
};

const mapChoiceListItemForChoiceListsSettings = (item: any) => ({
  ...item,
  Id: item.Id ?? "",
  Description: item.Description ?? "",
  Name: item.Name ?? "",
  Service: item.Service ?? "",
  MappedToDefault: item.MappedToDefault ?? "",
  Taxonomy: item.Taxonomy ?? "",
  PlaceOfService: item.PlaceOfService ?? "",
  TaxId: item.TaxId ? normalizeTaxId(item.TaxId) : "",
});

/** Parent entity for `/api/{Facilities|Practices}/{viewId}/{choiceListName}` choice-list settings APIs */
type ChoiceListSettingsParentScope = "facility" | "practice";

const choiceListSettingsParentApiSegment: Record<ChoiceListSettingsParentScope, string> = {
  facility: "Facilities",
  practice: "Practices",
};

const getChoiceListDataForParentChoiceListsSettings = async (
  scope: ChoiceListSettingsParentScope,
  parentViewId: string,
  choiceListName: string,
): Promise<ChoiceListDataItem[]> => {
  const segment = choiceListSettingsParentApiSegment[scope];
  const response = await get(
    `${baseURL}/api/${segment}/${parentViewId}/${choiceListName}?useOData=false&SortAsc.Id=1`,
    { useExactPath: true },
  );

  return response.body.Items.map((item: any) => mapChoiceListItemForChoiceListsSettings(item));
};

const getChoiceListDataForChoiceListsSettings = (
  facilityViewId: string,
  choiceListName: string,
): Promise<ChoiceListDataItem[]> =>
  getChoiceListDataForParentChoiceListsSettings("facility", facilityViewId, choiceListName);

const getChoiceListDataForPracticeChoiceListsSettings = (
  practiceViewId: string,
  choiceListName: string,
): Promise<ChoiceListDataItem[]> =>
  getChoiceListDataForParentChoiceListsSettings("practice", practiceViewId, choiceListName);

const getChoiceListDefaultValuesForChoiceListsSettings = async (
  choiceListName: string,
): Promise<ChoiceListDefaultValue[]> => {
  const response = await get(
    `${process.env.REACT_APP_TEE_GRP}/groupinginfo/choicelists/${choiceListName}/custom`,
    {
      useExactPath: true,
    },
  );

  return response.body?.Items || [];
};

const updateChoiceListDataForParentChoiceListsSettings = async (
  scope: ChoiceListSettingsParentScope,
  parentViewId: string,
  choiceListName: string,
  data: ChoiceListDataItem,
): Promise<ChoiceListDataItem> => {
  const segment = choiceListSettingsParentApiSegment[scope];
  const response = await post(`${baseURL}/api/${segment}/${parentViewId}/${choiceListName}`, data, {
    useExactPath: true,
  });

  return mapChoiceListItemForChoiceListsSettings(response.body);
};

const updateChoiceListDataForChoiceListsSettings = (
  facilityViewId: string,
  choiceListName: string,
  data: ChoiceListDataItem,
): Promise<ChoiceListDataItem> =>
  updateChoiceListDataForParentChoiceListsSettings(
    "facility",
    facilityViewId,
    choiceListName,
    data,
  );

const updateChoiceListDataForPracticeChoiceListsSettings = (
  practiceViewId: string,
  choiceListName: string,
  data: ChoiceListDataItem,
): Promise<ChoiceListDataItem> =>
  updateChoiceListDataForParentChoiceListsSettings(
    "practice",
    practiceViewId,
    choiceListName,
    data,
  );

const getPayerFlags = (): Promise<IdDescriptionBase[]> => {
  const url = `${baseURL}/api/choicelists/payerflags`;

  const extraOptions = {
    useExactPath: true,
  };

  return get(url, extraOptions).then((result) => {
    const payerFlags: IdDescriptionBase[] = (result.body || []).map((flag: any) => ({
      id: flag.Id,
      title: flag.Description,
      // GUI-2192: use ViewId
      ViewId: flag.ViewId,
    }));

    return orderBy(payerFlags, [{ field: "id", dir: "asc" }]);
  });
};

const getPlaceOfServiceChoiceList = async (): Promise<ChoiceListItem[]> => {
  const response = await get(`${process.env.REACT_APP_API_PATH}/api/ChoiceLists/PlacesOfService`, {
    useExactPath: true,
  });

  return response.body || [];
};

/**
 * Provider Specialty types (MCA-1327 / TEC-7236).
 * GET {TEE_GRP}/PhysicianFeeSchedule/specialtytypes → string[]
 */
const getProviderSpecialtyTypes = async (): Promise<string[]> => {
  const response = await get(`${groupingURL}/PhysicianFeeSchedule/specialtytypes`, {
    useExactPath: true,
  });

  return Array.isArray(response.body) ? response.body : [];
};

const loadFooterFieldsList = (grouperType: string, pricerType: string): Promise<FooterField[]> => {
  const url = `${groupingURL}/GroupingFields/${grouperType}/${pricerType || "None"}`;

  const extraOptions = {
    useExactPath: true,
  };

  return get(url, extraOptions).then((result) => result.body);
};

const getBillingNotes = (): Promise<IdDescriptionBase[]> => {
  const url = `${groupingURL}/groupinginfo/choiceLists/billingNotes/custom`;

  const extraOptions = {
    useExactPath: true,
  };

  return get(url, extraOptions).then((result) => {
    const items = result.body?.Items || [];
    const billingNotes: IdDescriptionBase[] = items.map((note: any) => ({
      id: note.Code,
      title: note.Description,
    }));

    return orderBy(billingNotes, [{ field: "id", dir: "asc" }]);
  });
};

const importProvidersForFacility = async (
  facilityId: string,
  file: File,
  format: string,
  doReplace: boolean,
): Promise<ProvidersImportJob> => {
  const url = `${baseURL}/api/facilities/${facilityId}/Providers/import?formatName=${format}&doReplace=${doReplace}`;
  const formData = new FormData();
  formData.append("file", file);

  const extraOptions = {
    useExactPath: true,
    skipContentTypeHeader: true,
  };

  return post(url, formData, extraOptions).then((result) => result.body);
};

const getProvidersImportStatus = async (
  facilityId: string,
  jobId: string,
): Promise<ProvidersImportJob> => {
  const url = `${baseURL}/api/facilities/${facilityId}/Providers/import/${jobId}`;

  return get(url, { useExactPath: true }).then((result) => result.body);
};

const cancelProvidersImport = async (facilityId: string, jobId: string): Promise<string> => {
  const url = `${baseURL}/api/facilities/${facilityId}/Providers/import/${jobId}`;

  return deleteEndpoint(url, { useExactPath: true }).then((result) => result.body);
};

const getConversionFactorTypes = async (): Promise<ConversionFactorTypeItem[]> => {
  const response = await get(`${groupingURL}/PhysicianFeeSchedule/ConversionFactorTypes`, {
    useExactPath: true,
  });

  return response.body;
};

const getProviderConversionFactors = async (facilityId: string, providerId: string) => {
  const response = await get(
    `${baseURL}/api/Facilities/${facilityId}/Providers/${providerId}/ConversionFactors`,
    {
      useExactPath: true,
    },
  );

  return response.body;
};

const addProviderConversionFactor = async (
  newItem: ConversionFactorItem,
  facilityId: string,
  providerId: string,
) => {
  const response = await post(
    `${baseURL}/api/Facilities/${facilityId}/Providers/${providerId}/ConversionFactors`,
    {
      Type: newItem.Type,
      EffectiveDate: newItem.EffectiveDate,
      ExpirationDate: newItem.ExpirationDate,
    },
    {
      useExactPath: true,
    },
  );

  return response.body;
};

const updateProviderConversionFactor = async (
  updatedItem: ConversionFactorItem,
  facilityId: string,
  providerId: string,
) => {
  const response = await post(
    `${baseURL}/api/Facilities/${facilityId}/Providers/${providerId}/ConversionFactors`,
    {
      ViewId: updatedItem.ViewId,
      Type: updatedItem.Type,
      EffectiveDate: updatedItem.EffectiveDate,
      ExpirationDate: updatedItem.ExpirationDate,
      Version: updatedItem.Version,
    },
    {
      useExactPath: true,
      headers: { "If-Match": updatedItem.Version },
    },
  );

  return response.body;
};

const removeProviderConversionFactor = async (
  itemViewId: string,
  facilityId: string,
  providerId: string,
) => {
  const response = await deleteEndpoint(
    `${baseURL}/api/Facilities/${facilityId}/Providers/${providerId}/ConversionFactors/${itemViewId}`,
    {
      useExactPath: true,
    },
  );

  return response.body;
};

export const choiceListsAPI = {
  getChoiceListData,
  getChoiceListDataByFacility,
  getChoiceListDataForChoiceListsSettings,
  getChoiceListDataForPracticeChoiceListsSettings,
  getChoiceListDefaultValuesForChoiceListsSettings,
  getFacilities,
  getInitialChoiceListDataByFacility,
  getPlaceOfServiceChoiceList,
  getProviderSpecialtyTypes,
  loadFooterFieldsList,
  updateChoiceListDataForChoiceListsSettings,
  updateChoiceListDataForPracticeChoiceListsSettings,
  importProvidersForFacility,
  getProvidersImportStatus,
  cancelProvidersImport,
  getConversionFactorTypes,
  getProviderConversionFactors,
  addProviderConversionFactor,
  updateProviderConversionFactor,
  removeProviderConversionFactor,
};
